import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Trader Documents Page
 * KYC document upload for account verification
 */
import { useState, useEffect, useRef } from 'react';
import { useTraderAuth, traderAPI } from '../context/TraderAuthContext';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import TraderLayout from '../components/TraderLayout';
import { toast } from 'sonner';
import { 
  Upload, 
  FileText, 
  Image, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Trash2,
  Eye,
  X,
  Loader2,
  Shield,
  AlertCircle,
  CreditCard,
  Home,
  User,
  Camera
} from 'lucide-react';

const TraderDocuments = () => {
  const tx = useTraderText();
  const { user } = useTraderAuth();
  const { isDark } = useTraderTheme();
  const { t } = useTraderLanguage();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [kycStatus, setKycStatus] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Document types with their requirements
  const documentTypes = [
    { 
      id: 'id_front', 
      label: t('documents.idFront'), 
      description: t('documents.idFrontDesc'),
      icon: CreditCard,
      required: true
    },
    { 
      id: 'id_back', 
      label: t('documents.idBack'), 
      description: t('documents.idBackDesc'),
      icon: CreditCard,
      required: true
    },
    { 
      id: 'proof_of_address', 
      label: t('documents.proofOfAddress'), 
      description: t('documents.proofOfAddressDesc'),
      icon: Home,
      required: true
    },
    { 
      id: 'selfie', 
      label: t('documents.selfie'), 
      description: t('documents.selfieDesc'),
      icon: Camera,
      required: true
    },
    { 
      id: 'passport', 
      label: t('documents.passport'), 
      description: t('documents.passportDesc'),
      icon: FileText,
      required: false
    },
  ];

  // Fetch documents and KYC status
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [docsRes, kycRes] = await Promise.all([
          traderAPI.get('/api/trader/documents'),
          traderAPI.get('/api/trader/kyc-status')
        ]);
        setDocuments(docsRes.data);
        setKycStatus(kycRes.data);
      } catch (error) {
        console.error('Error fetching documents:', error);
        toast.error(tx(t('documents.fetchError')));
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [t, tx]);

  // Get document status for a type
  const getDocumentForType = (type) => {
    return documents.find(doc => doc.type === type);
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedType) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(tx(t('documents.invalidFileType')));
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(tx(t('documents.fileTooLarge')));
      return;
    }
    
    setUploading(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const response = await traderAPI.post('/api/trader/documents', {
            document_type: selectedType,
            document_data: reader.result,
            content_type: file.type,
            filename: file.name
          });
          
          if (response.data.success) {
            toast.success(tx(t('documents.uploadSuccess')));
            
            // Add new document to list
            setDocuments(prev => [...prev, response.data.document]);
            
            // Update KYC status
            const kycRes = await traderAPI.get('/api/trader/kyc-status');
            setKycStatus(kycRes.data);
          }
        } catch (error) {
          toast.error(tx(error.response?.data?.detail || t('documents.uploadError')));
        } finally {
          setUploading(false);
          setSelectedType(null);
        }
      };
      reader.onerror = () => {
        toast.error(tx(t('documents.uploadError')));
        setUploading(false);
        setSelectedType(null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error(tx(t('documents.uploadError')));
      setUploading(false);
      setSelectedType(null);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle document deletion
  const handleDelete = async (docId) => {
    try {
      const response = await traderAPI.delete(`/api/trader/documents/${docId}`);
      
      if (response.data.success) {
        toast.success(tx(t('documents.deleteSuccess')));
        setDocuments(prev => prev.filter(doc => doc.id !== docId));
        
        // Update KYC status
        const kycRes = await traderAPI.get('/api/trader/kyc-status');
        setKycStatus(kycRes.data);
      }
    } catch (error) {
      toast.error(tx(error.response?.data?.detail || t('documents.deleteError')));
    }
  };

  // Preview document
  const handlePreview = async (docId) => {
    setPreviewLoading(true);
    try {
      const response = await traderAPI.get(`/api/trader/documents/${docId}`);
      setPreviewDoc(response.data);
    } catch (error) {
      toast.error(tx(t('documents.previewError')));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
  const tx = useTraderText();
    const config = {
      pending: { icon: Clock, color: 'text-yellow-500 bg-yellow-500/10', label: t('documents.statusPending') },
      approved: { icon: CheckCircle, color: 'text-green-500 bg-green-500/10', label: t('documents.statusApproved') },
      rejected: { icon: XCircle, color: 'text-red-500 bg-red-500/10', label: t('documents.statusRejected') }
    };
    
    const { icon: Icon, color, label } = config[status] || config.pending;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        <Icon size={12} />
        {tx(label)}
      </span>
    );
  };

  // KYC Status Card
  const KYCStatusCard = () => {
  const tx = useTraderText();
    if (!kycStatus) return null;
    
    const statusConfig = {
      not_started: { color: 'bg-gray-500', label: t('documents.kycNotStarted') },
      pending: { color: 'bg-yellow-500', label: t('documents.kycPending') },
      approved: { color: 'bg-green-500', label: t('documents.kycApproved') },
      rejected: { color: 'bg-red-500', label: t('documents.kycRejected') }
    };
    
    const { color, label } = statusConfig[kycStatus.kyc_status] || statusConfig.not_started;
    const uploadedCount = kycStatus.total_documents || 0;
    const requiredCount = kycStatus.required_documents?.length || 4;
    
    return (
      <div className={`rounded-xl p-6 mb-6 ${isDark ? 'bg-[#151d2b]' : 'bg-white shadow-sm border border-gray-200'}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center`}>
              <Shield size={24} className="text-white" />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('documents.verificationStatus')}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {tx(label)}
              </p>
            </div>
          </div>
          <div className={`text-right ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <p className="text-sm">{t('documents.documentsUploaded')}</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {uploadedCount} / {requiredCount}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <TraderLayout>
      <div className="h-full overflow-auto p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('nav.uploadDocuments')}
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('documents.subtitle')}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-cyan-500' : 'text-blue-500'}`} />
            </div>
          ) : (
            <>
              {/* KYC Status Card */}
              <KYCStatusCard />

              {/* Info Alert */}
              <div className={`flex items-start gap-3 p-4 rounded-lg mb-6 ${
                isDark ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-blue-50 border border-blue-200'
              }`}>
                <AlertCircle className={isDark ? 'text-cyan-400 flex-shrink-0' : 'text-blue-500 flex-shrink-0'} size={20} />
                <p className={`text-sm ${isDark ? 'text-cyan-300' : 'text-blue-700'}`}>
                  {t('documents.infoText')}
                </p>
              </div>

              {/* Document Upload Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {documentTypes.map((docType) => {
                  const existingDoc = getDocumentForType(docType.id);
                  const Icon = docType.icon;
                  
                  return (
                    <div 
                      key={docType.id}
                      className={`rounded-xl p-5 ${isDark ? 'bg-[#151d2b]' : 'bg-white shadow-sm border border-gray-200'}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          existingDoc?.status === 'approved' 
                            ? 'bg-green-500/10 text-green-500'
                            : existingDoc?.status === 'rejected'
                              ? 'bg-red-500/10 text-red-500'
                              : existingDoc
                                ? 'bg-yellow-500/10 text-yellow-500'
                                : isDark 
                                  ? 'bg-gray-800 text-gray-400' 
                                  : 'bg-gray-100 text-gray-500'
                        }`}>
                          <Icon size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {tx(docType.label)}
                            </h4>
                            {docType.required && (
                              <span className="text-red-500 text-xs">*</span>
                            )}
                          </div>
                          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {tx(docType.description)}
                          </p>
                          
                          {existingDoc ? (
                            <div className="mt-3">
                              <div className="flex items-center justify-between">
                                <StatusBadge status={existingDoc.status} />
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handlePreview(existingDoc.id)}
                                    className={`p-1.5 rounded-lg transition-colors ${
                                      isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                                    }`}
                                    title={t('documents.preview')}
                                  >
                                    <Eye size={16} />
                                  </button>
                                  {existingDoc.status !== 'approved' && (
                                    <button
                                      onClick={() => handleDelete(existingDoc.id)}
                                      className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors"
                                      title={t('documents.delete')}
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  )}
                                </div>
                              </div>
                              {existingDoc.status === 'rejected' && existingDoc.rejection_reason && (
                                <p className="text-xs text-red-400 mt-2">
                                  {existingDoc.rejection_reason}
                                </p>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedType(docType.id);
                                fileInputRef.current?.click();
                              }}
                              disabled={uploading}
                              className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                isDark
                                  ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                              } disabled:opacity-50`}
                              data-testid={`upload-${docType.id}`}
                            >
                              {uploading && selectedType === docType.id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Upload size={16} />
                              )}
                              {t('documents.upload')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="document-input"
              />
            </>
          )}
        </div>
      </div>

      {/* Document Preview Modal */}
      {previewDoc && (
        <div 
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewDoc(null)}
        >
          <div 
            className={`max-w-3xl w-full max-h-[90vh] rounded-xl overflow-hidden ${
              isDark ? 'bg-[#151d2b]' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-4 border-b ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {previewDoc.filename}
              </h3>
              <button
                onClick={() => setPreviewDoc(null)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-cyan-500' : 'text-blue-500'}`} />
                </div>
              ) : previewDoc.content_type?.startsWith('image/') ? (
                <img 
                  src={previewDoc.data} 
                  alt={previewDoc.filename}
                  className="max-w-full h-auto mx-auto rounded-lg"
                />
              ) : previewDoc.content_type === 'application/pdf' ? (
                <iframe
                  src={previewDoc.data}
                  className="w-full h-[70vh] rounded-lg"
                  title={previewDoc.filename}
                />
              ) : (
                <div className="text-center py-12">
                  <FileText size={48} className={isDark ? 'text-gray-600 mx-auto' : 'text-gray-400 mx-auto'} />
                  <p className={`mt-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('documents.cannotPreview')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </TraderLayout>
  );
};

export default TraderDocuments;
